/**
 * Hook for managing user preferences stored on the server.
 *
 * Replaces localStorage for:
 * - Active project ID
 * - Open tabs and active tab
 * - Expanded folders per project
 */
import { create } from 'zustand';
import { usersApi, type UserPreferences } from '../services/api';
import { sanitizePreferences } from '../lib/preferences';

interface UserPreferencesState {
  preferences: UserPreferences;
  isLoading: boolean;
  isInitialized: boolean;

  // Actions
  initialize: () => Promise<void>;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  setPreferences: (preferences: UserPreferences) => void;
}

export const useUserPreferencesStore = create<UserPreferencesState>((set, get) => ({
  preferences: {},
  isLoading: false,
  isInitialized: false,

  initialize: async () => {
    // Skip if already initialized or loading
    if (get().isInitialized || get().isLoading) return;

    set({ isLoading: true });
    try {
      const user = await usersApi.me();
      set({
        preferences: sanitizePreferences(user.preferences),
        isInitialized: true,
        isLoading: false,
      });
    } catch (error) {
      // User not found or not authenticated - that's OK, use defaults
      console.warn('Failed to load user preferences:', error);
      set({
        preferences: {},
        isInitialized: true,
        isLoading: false,
      });
    }
  },

  // This tab's copy is the truth for what it writes. Writes of different keys
  // run at once (Recent and the drawer after a turn), so a response is never
  // adopted whole: it is a snapshot from when the server merged it, and one
  // arriving after a later write would put back that write's old value.
  updatePreferences: async (updates: Partial<UserPreferences>) => {
    const before = get().preferences;
    set({ preferences: { ...before, ...updates } });

    try {
      await usersApi.updatePreferences(updates);
    } catch (error) {
      // Revert only what this write set, and only where nothing wrote since.
      console.error('Failed to update preferences:', error);
      const now: Record<string, unknown> = { ...get().preferences };
      const prior: Record<string, unknown> = { ...before };
      for (const [key, value] of Object.entries(updates)) {
        if (now[key] === value) now[key] = prior[key];
      }
      set({ preferences: now as UserPreferences });
    }
  },

  setPreferences: (preferences: UserPreferences) => {
    set({ preferences: sanitizePreferences(preferences) });
  },
}));

/**
 * Hook to access user preferences and update functions.
 * Auto-initializes on first use if not already initialized.
 */
export function useUserPreferences() {
  const store = useUserPreferencesStore();

  return {
    preferences: store.preferences,
    isLoading: store.isLoading,
    isInitialized: store.isInitialized,
    updatePreferences: store.updatePreferences,
    initialize: store.initialize,
  };
}

/**
 * Selector hooks for specific preferences to avoid unnecessary re-renders.
 */
export function useActiveProjectIdPreference() {
  return useUserPreferencesStore((state) => state.preferences.activeProjectId);
}

export function useProjectTabsPreference(projectId: string | undefined) {
  return useUserPreferencesStore((state) =>
    projectId ? state.preferences.tabsByProject?.[projectId] : undefined
  );
}

export function useExpandedFoldersPreference(projectId: string | undefined) {
  return useUserPreferencesStore((state) =>
    projectId ? state.preferences.expandedFolders?.[projectId] : undefined
  );
}
